import React from "react";
import {AxisConfig, BarChart, BarLabelProps, ChartsYAxisProps, useAnimate} from "@mui/x-charts";
import {styled} from "@mui/material";
import {interpolateObject} from "@mui/x-charts-vendor/d3-interpolate";
import {ChartsTextProps} from "@mui/x-charts/ChartsText";
import {PerformanceByIndicatorsResource} from "../../resources/dashboard.resource";

const valueFormatter = (value: number | null) => {
    if (value === null || value === undefined) {
        return '0%';
    }
    return `${Math.round(value)} %`;
}

const Text = styled('text')(({theme}) => ({
    ...theme?.typography?.body2,
    stroke: 'none',
    fill: theme?.palette?.text?.primary,
    transition: 'opacity 0.2s ease-in, fill 0.2s ease-in',
    textAnchor: 'middle',
    dominantBaseline: 'central',
    pointerEvents: 'none',
}));

const BarLabel = (props: BarLabelProps) => {
    const {
        seriesId,
        dataIndex,
        color,
        isFaded,
        isHighlighted,
        classes,
        xOrigin,
        yOrigin,
        x,
        y,
        width,
        height,
        layout,
        skipAnimation,
        ...otherProps
    } = props;
    const animatedProps = useAnimate(
        {x: x + 0.9 * width, y: y + height / 4},
        {
            initialProps: {x: xOrigin, y: yOrigin},
            createInterpolator: interpolateObject,
            transformProps: (p) => p,
            applyProps: (element: SVGTextElement, p) => {
                element.setAttribute('x', p.x.toString());
                element.setAttribute('y', p.y.toString());
            },
            skip: skipAnimation,
        },
    );

    if (seriesId === 'notCompleted') {
        return '';
    }
    const value = otherProps.children as number | null;
    if (value === null || value < 5) {
        return '';
    }
    otherProps.children = `${Math.round(value)} %`;
    return (
        <Text {...otherProps} fill={color} textAnchor="middle" {...animatedProps} />
    );
}

const CustomTickLabel = (props: ChartsTextProps) => {
    const {x, y, transform, children, ownerState, text, ...rest} = props;
    return (
        <text
            x={x}
            y={y}
            transform={transform}
            dominantBaseline="central"
            style={{fill: "red"}}
            {...rest}
        >
            <tspan x={5} dy="0px" dominantBaseline="central">
                {text}
            </tspan>
        </text>
    );
}

interface IProps {
    performanceByIndicators: PerformanceByIndicatorsResource[],
}

const PerformanceByIndicatorsBarChart = ({performanceByIndicators}: IProps) => {
    return (
        <BarChart
            dataset={performanceByIndicators as Record<string, any>[]}
            xAxis={
                [{
                    tickLabelStyle: {display: "none"},
                    disableTicks: true,
                    disableLine: true,

                }]}
            yAxis={[{
                scaleType: 'band',
                dataKey: 'indicatorName',
                disableTicks: true,
                disableLine: true,
                barGapRatio: 0.1,
                categoryGapRatio: 0.1,
                width: 0,
                tickLabelStyle: {
                    textAnchor: 'start',
                    dominantBaseline: 'central',
                    fontSize: 16,
                    fontWeight: 500,
                    fill: '#000000',
                },
            } as AxisConfig<'band', string, ChartsYAxisProps>,]}
            series={[{
                id: 'completed',
                dataKey: 'completed',
                stack: 'indicators',
                color: '#7ECC8B',
                valueFormatter,
                label: 'Виконано'
            }, {
                id: 'notCompleted',
                dataKey: 'notCompleted',
                stack: 'indicators',
                color: '#E2ECF2',
                valueFormatter,
                label: 'Не виконано'
            }]}
            barLabel="value"
            slots={{barLabel: BarLabel, axisTickLabel: CustomTickLabel}}
            layout="horizontal"
            height={700}
            borderRadius={20}
            hideLegend={true}
            sx={{
                '& .MuiBarLabel-root': {
                    fontSize: 16,
                    fontWeight: 700,
                    fill: "#000000",
                },
            }}
        />
    );
};

export default PerformanceByIndicatorsBarChart;
